/**
 * Flota — trasy i integracja z systemem tras (`fleet.web.routes.*`, `fleet.web.integration.*`).
 * Źródłem tras jest wyłącznie import pliku CSV; system tras nie jest podłączony (docs/FLEET-ROUTES.md).
 */
export type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetRoutesPl = {
  routes: {
    title: 'Trasy planowane',
    description: 'Trasy z planera Routimo (ERP) i z importu pliku CSV porównane z przebiegiem z kart drogowych. Rzeczywiste km pochodzą z odczytów licznika, nigdy z planu.',
    sourceNote: 'Źródła tras: planer Routimo z bazy ERP (import erp:logistics) i import pliku CSV.',
    sourceNoteLink: 'Opis formatu i import',
    chipRoutes: 'Trasy',
    chipCompared: 'Porównane',
    chipAbove: 'Powyżej planu',
    chipNoActual: 'Bez wykonania',
    search: 'Szukaj po nazwie lub identyfikatorze trasy…',
    empty: 'Brak tras w wybranym okresie. Zaimportuj plik CSV, aby je zobaczyć.',
    emptyFiltered: 'Żadna trasa nie pasuje do filtrów.',
    emptyImport: 'Przejdź do importu',
    loadFailed: 'Nie udało się wczytać tras.',
    filters: {
      from: 'Od',
      to: 'Do',
      vehicle: 'Pojazd',
      allVehicles: 'Wszystkie pojazdy',
      driver: 'Kierowca',
      allDrivers: 'Wszyscy kierowcy',
      status: 'Status trasy',
      allStatuses: 'Wszystkie statusy',
      compare: 'Plan a wykonanie',
      allCompare: 'Każdy wynik',
      logbook: 'Karta drogowa',
      allLogbook: 'Z kartą i bez',
      linked: 'Z powiązaną kartą',
      unlinked: 'Bez powiązanej karty',
      unmapped: 'Tylko bez pojazdu',
      unapproved: 'Uwzględnij niezaakceptowane karty',
      cancelled: 'Pokaż anulowane',
    },
    status: { PLANNED: 'Zaplanowana', IN_PROGRESS: 'W toku', COMPLETED: 'Zakończona', CANCELLED: 'Anulowana' },
    plan: {
      OK: 'W granicach planu',
      ABOVE_PLAN: 'Powyżej planu',
      BELOW_PLAN: 'Poniżej planu',
      NO_PLAN: 'Brak km planowanych',
      NO_ACTUAL: 'Brak wykonania',
      NO_DATA: 'Brak danych',
    },
    planHelp: {
      OK: 'Odchylenie mieści się w tolerancji ustawionej we flocie ({tolerance}%).',
      ABOVE_PLAN: 'Przebieg z kart jest większy od planu o więcej niż tolerancja ({tolerance}%).',
      BELOW_PLAN: 'Przebieg z kart jest mniejszy od planu o więcej niż tolerancja ({tolerance}%).',
      NO_PLAN: 'System tras nie podał km planowanych — nie ma z czym porównać.',
      NO_ACTUAL: 'Brak powiązanej karty drogowej z przebiegiem (lub karta nie jest zaakceptowana).',
      NO_DATA: 'Brak zarówno km planowanych, jak i przebiegu z kart.',
    },
    summary: {
      title: 'Plan a wykonanie',
      routes: 'Trasy',
      compared: 'Porównane',
      within: 'W granicach planu',
      above: 'Powyżej planu',
      below: 'Poniżej planu',
      noPlan: 'Bez km planowanych',
      noActual: 'Bez wykonania',
      plannedKm: 'Km planowane',
      actualKm: 'Km rzeczywiste',
      tolerance: 'Tolerancja: {tolerance}% (ustawienia floty)',
      mode: { APPROVED_ONLY: 'Do wykonania liczą się tylko zaakceptowane karty drogowe.', INCLUDING_UNAPPROVED: 'Do wykonania liczą się także karty wysłane i niezgodne (wynik wstępny).' },
    },
    col: { date: 'Data', route: 'Trasa', status: 'Status', vehicle: 'Pojazd', driver: 'Kierowca', stops: 'Punkty', planned: 'Km planowane', actual: 'Km rzeczywiste', deviation: 'Odchylenie', result: 'Plan a wykonanie', logbooks: 'Karty' },
    noVehicle: 'bez pojazdu',
    noDriver: 'bez kierowcy',
    externalValue: 'z pliku: {value}',
    noLogbook: 'brak karty',
    autoLink: 'Powiąż automatycznie',
    autoLinkHint: 'Łączy trasę z kartą tylko wtedy, gdy w dniu trasy jest dokładnie jedna niepowiązana karta tego pojazdu.',
    autoLinkDone: 'Powiązano {linked}, niejednoznaczne {ambiguous}, bez kandydata {none} (przeanalizowano {routes} tras).',
    detail: {
      back: 'Wróć do tras',
      loadFailed: 'Nie udało się wczytać trasy.',
      notFound: 'Trasa nie istnieje albo nie masz do niej dostępu.',
      source: 'Źródło',
      sourceCsv: 'Import pliku CSV',
      sourceRoutimo: 'Planer tras Routimo (ERP)',
      externalId: 'Identyfikator w systemie źródłowym',
      date: 'Data realizacji',
      window: 'Planowane okno',
      lastRun: 'Ostatni import',
      comparison: 'Plan a wykonanie',
      plannedKm: 'Km planowane',
      actualKm: 'Km rzeczywiste (karty)',
      deviationKm: 'Odchylenie',
      deviationPct: 'Odchylenie %',
      tolerance: 'Tolerancja',
      stops: 'Punkty dostaw',
      stopsEmpty: 'Trasa nie ma punktów dostaw w pliku.',
      stopCol: { seq: 'Lp.', id: 'Id punktu', name: 'Nazwa', address: 'Adres', planned: 'Planowany czas' },
      logbooks: 'Powiązane karty drogowe',
      logbooksEmpty: 'Brak powiązanych kart drogowych.',
      logbookKm: '{km} km',
      noKm: 'bez przebiegu',
      unlink: 'Rozłącz',
      unlinkDone: 'Karta rozłączona z trasą.',
      candidates: 'Karty do powiązania',
      candidatesHint: 'Karty tego pojazdu z dnia trasy, jeszcze niepowiązane z żadną trasą.',
      candidatesEmpty: 'Brak niepowiązanych kart tego pojazdu z dnia trasy.',
      candidatesNoVehicle: 'Trasa nie ma przypisanego pojazdu — uzupełnij mapowanie identyfikatorów w integracji.',
      link: 'Powiąż',
      linkDone: 'Karta powiązana z trasą.',
      openLogbook: 'Karta drogowa',
      openVehicle: 'Karta pojazdu',
      mapIds: 'Uzupełnij mapowanie',
      unmappedVehicle: 'Pojazd z pliku „{value}” nie jest zmapowany.',
      unmappedDriver: 'Kierowca z pliku „{value}” nie jest zmapowany.',
    },
  },
  integration: {
    title: 'Integracja tras',
    description: 'Import planowanych tras z pliku CSV, dziennik przebiegów i mapowanie identyfikatorów pojazdów oraz kierowców.',
    banner: {
      title: 'Źródło danych: import pliku CSV',
      body: 'Nie znamy jeszcze nazwy ani API systemu tras, więc nic nie jest pobierane automatycznie — trasy trafiają do Floty wyłącznie z wgranego pliku. Karty drogowe, tankowania i analityka działają bez tej integracji. Gdy system tras zostanie wskazany, ten ekran zacznie pokazywać jego synchronizacje bez zmian po Waszej stronie.',
    },
    tabs: { import: 'Import', runs: 'Przebiegi', idmap: 'Mapowanie identyfikatorów', format: 'Format pliku' },
    import: {
      title: 'Import pliku CSV',
      description: 'Najpierw sprawdź plik w trybie podglądu (nic nie zostanie zapisane), potem zaimportuj. Powtórny import tej samej trasy nie tworzy duplikatu.',
      choose: 'Wybierz plik CSV',
      drop: 'Plik CSV (UTF-8 lub Windows-1250), do 5 MB',
      selected: 'Wybrany plik: {name} ({size})',
      clear: 'Usuń wybór',
      preview: 'Podgląd (bez zapisu)',
      run: 'Importuj',
      confirmTitle: 'Zaimportować plik?',
      confirmBody: 'Trasy z pliku zostaną zapisane lub zaktualizowane. Trasa z choć jednym błędnym wierszem jest pomijana w całości.',
      problem: { extension: 'Wybierz plik z rozszerzeniem .csv.', tooLarge: 'Plik jest większy niż 5 MB.', empty: 'Plik jest pusty.' },
      resultPreview: 'Wynik podglądu — nic nie zapisano',
      resultImport: 'Wynik importu',
      failedTitle: 'Import nie powiódł się',
      failedRun: 'Zobacz przebieg w dzienniku',
      doneImport: 'Import zakończony.',
      donePreview: 'Podgląd gotowy — nic nie zapisano.',
      importAfterPreview: 'Importuj ten plik',
      missingColumns: 'Szczegóły: {details}',
    },
    report: {
      status: 'Status',
      file: 'Plik',
      startedAt: 'Rozpoczęto',
      finishedAt: 'Zakończono',
      triggeredBy: 'Uruchomił',
      attempts: 'Próby',
      dryRun: 'Podgląd (bez zapisu)',
      rowsTotal: 'Wiersze',
      rowsRejected: 'Odrzucone wiersze',
      routesCreated: 'Nowe trasy',
      routesUpdated: 'Zaktualizowane',
      routesUnchanged: 'Bez zmian',
      routesUnmapped: 'Bez pojazdu',
      errors: 'Błędy per wiersz',
      errorsNone: 'Brak błędów w wierszach.',
      errorsTruncated: 'Pokazano pierwsze 200 błędów — pozostałe zostały pominięte w raporcie.',
      errCol: { row: 'Wiersz', column: 'Kolumna', code: 'Kod', message: 'Szczegóły techniczne (EN)' },
      unmapped: 'Niezmapowane identyfikatory',
      unmappedHint: 'Te wartości z pliku nie pasują do żadnego pojazdu ani kierowcy. Trasy zostały zapisane bez nich — nic nie jest zgadywane.',
      unmappedNone: 'Wszystkie identyfikatory zostały dopasowane.',
      unmappedCol: { kind: 'Rodzaj', value: 'Wartość z pliku', routes: 'Trasy', action: 'Akcja' },
      mapNow: 'Zmapuj',
      kind: { VEHICLE: 'Pojazd', DRIVER: 'Kierowca' },
      runStatus: { RUNNING: 'W toku', SUCCESS: 'Sukces', PARTIAL: 'Częściowy', FAILED: 'Nieudany' },
      errorCodes: {
        ROUTE_ID_REQUIRED: 'Brak identyfikatora trasy',
        ROUTE_DATE_INVALID: 'Niepoprawna data trasy',
        STOP_SEQUENCE_INVALID: 'Niepoprawny numer punktu',
        STOP_SEQUENCE_DUPLICATE: 'Powtórzony numer punktu',
        PLANNED_KM_INVALID: 'Niepoprawne km planowane',
        DATETIME_INVALID: 'Niepoprawna data lub godzina',
        STATUS_INVALID: 'Nieznany status trasy',
        ROUTE_FIELD_CONFLICT: 'Sprzeczne wartości w jednej trasie',
        ROW_TOO_LONG: 'Wiersz lub wartość zbyt długa',
        ROUTE_SKIPPED: 'Trasa pominięta w całości',
      },
    },
    runs: {
      title: 'Dziennik przebiegów',
      description: 'Każdy import (także podgląd) zostawia wpis z licznikami i błędami.',
      empty: 'Brak przebiegów — import nie był jeszcze uruchamiany.',
      filterStatus: 'Status przebiegu',
      allStatuses: 'Wszystkie statusy',
      loadFailed: 'Nie udało się wczytać dziennika.',
      col: { started: 'Rozpoczęto', file: 'Plik', status: 'Status', rows: 'Wiersze', rejected: 'Odrzucone', created: 'Nowe', updated: 'Zaktualizowane', unchanged: 'Bez zmian', unmapped: 'Bez pojazdu', by: 'Uruchomił' },
      detailTitle: 'Przebieg synchronizacji',
      back: 'Wróć do integracji',
      notFound: 'Przebieg nie istnieje albo nie masz do niego dostępu.',
      loadDetailFailed: 'Nie udało się wczytać przebiegu.',
      runError: 'Komunikat błędu',
      source: 'Źródło: {source}',
    },
    idmap: {
      title: 'Mapowanie identyfikatorów',
      description: 'Łączy wartość z pliku (np. numer pojazdu w systemie tras) z pojazdem lub kierowcą Floty. Zmiana od razu łączy istniejące trasy.',
      add: 'Dodaj mapowanie',
      empty: 'Brak mapowań. Dopasowanie po numerze rejestracyjnym i e-mailu kierowcy działa bez nich.',
      loadFailed: 'Nie udało się wczytać mapowań.',
      filterKind: 'Rodzaj',
      allKinds: 'Pojazdy i kierowcy',
      col: { kind: 'Rodzaj', external: 'Wartość z pliku', target: 'Przypisano do', source: 'Źródło', action: 'Akcja' },
      remove: 'Usuń mapowanie',
      removeTitle: 'Usunąć mapowanie?',
      removeBody: 'Trasy powiązane tym mapowaniem stracą pojazd lub kierowcę, a wartość trafi na listę niezmapowanych.',
      removeDone: 'Mapowanie usunięte. Odłączone trasy: {count}.',
      dialogTitle: 'Nowe mapowanie',
      dialogDescription: 'Wskaż wartość dokładnie tak, jak występuje w pliku (wielkość liter i spacje na brzegach są pomijane).',
      kind: 'Rodzaj',
      external: 'Wartość z pliku',
      externalRequired: 'Podaj wartość z pliku.',
      pickVehicle: 'Pojazd Floty',
      pickDriver: 'Kierowca Floty',
      targetRequired: 'Wybierz pojazd lub kierowcę.',
      choose: 'Wybierz…',
      driversNeedRight: 'Lista kierowców wymaga uprawnienia do edycji pojazdów.',
      saved: 'Mapowanie zapisane. Połączone trasy: {count}.',
      relink: 'Powiąż ponownie trasy',
      relinkHint: 'Przelicza trasy bez pojazdu lub kierowcy, np. po dodaniu pojazdu do floty.',
      relinkDone: 'Ponownie powiązano trasy: {count}.',
      unmappedRoutes: 'Trasy bez pojazdu',
      unmappedRoutesHint: 'Trasy, których pojazd nie został rozpoznany — uzupełnij mapowanie lub dodaj pojazd do floty.',
      unmappedRoutesEmpty: 'Wszystkie trasy mają przypisany pojazd.',
      unmappedOpen: 'Zobacz trasy',
    },
    format: {
      title: 'Format pliku CSV',
      intro: 'Jeden wiersz to jeden punkt dostawy trasy (albo sama trasa, gdy kolumny punktu są puste). Nagłówek jest wymagany; kolejność kolumn dowolna, nieznane kolumny są ignorowane.',
      rules: {
        encoding: 'Kodowanie: UTF-8 (z BOM lub bez); plik zapisany w polskim Excelu (Windows-1250) też zostanie odczytany.',
        separator: 'Separator: średnik, przecinek lub tabulator — wykrywany po nagłówku. Wartości ze średnikiem lub nową linią ujmij w cudzysłów.',
        dates: 'Daty: RRRR-MM-DD lub DD.MM.RRRR. Godziny: ISO 8601 ze strefą albo czas lokalny bez strefy (traktowany jako Europe/Warsaw).',
        idempotent: 'Klucz trasy to jej identyfikator: ponowny import nie tworzy duplikatów, niezmienione trasy są pomijane.',
        skipped: 'Trasa z choć jednym błędnym wierszem jest pomijana w całości, żeby nie nadpisać poprawnej trasy uciętą listą punktów.',
        match: 'Pojazd: mapowanie → numer rejestracyjny. Kierowca: mapowanie → e-mail konta z rolą Kierowca. Brak dopasowania = trasa bez pojazdu / kierowcy.',
      },
      colName: 'Kolumna',
      colRequired: 'Wymagana',
      colMeaning: 'Znaczenie',
      required: 'tak',
      optional: 'nie',
      example: 'Przykład (separator „;”)',
      copy: 'Kopiuj przykład',
      copied: 'Skopiowano przykład.',
      download: 'Pobierz przykład',
      columns: {
        route_id: 'Identyfikator trasy w systemie źródłowym (do 500 znaków) — klucz idempotencji.',
        route_date: 'Data realizacji: RRRR-MM-DD lub DD.MM.RRRR.',
        status: 'PLANNED (domyślnie), IN_PROGRESS, COMPLETED lub CANCELLED.',
        vehicle_plate: 'Numer rejestracyjny — dopasowanie do pojazdu floty (bez wielkości liter, spacji i myślników).',
        vehicle_external_id: 'Identyfikator pojazdu w systemie tras — mapowany; ma pierwszeństwo przed numerem rejestracyjnym.',
        driver_email: 'E-mail aktywnego konta z rolą Kierowca.',
        driver_external_id: 'Identyfikator kierowcy w systemie tras — mapowany.',
        planned_km: 'Planowane km trasy: liczba z kropką lub przecinkiem, 0–5000.',
        planned_start: 'Planowany początek trasy.',
        planned_end: 'Planowany koniec trasy.',
        stop_sequence: 'Numer kolejny punktu (1–10000), unikalny w trasie; wymagany, gdy są dane punktu.',
        stop_id: 'Identyfikator punktu dostawy.',
        stop_name: 'Nazwa punktu dostawy.',
        stop_address: 'Adres punktu dostawy.',
        stop_planned_at: 'Planowany czas dostawy w punkcie.',
      },
    },
  },
};
